import FirebaseService from "../../services/firebase/FirebaseService";
import { store } from "../configureStore";
import {
  setTeacherClassrooms,
  addStudentToTeacher,
  removeStudentFromTeacher,
  finishLoadingTeacher,
  setSharedAssignments,
  setStarredAssignments,
  addAssignment,
  removeAssignment,
  addAssignmentClass,
  removeClassFromAssignmentClasses,
  removeAllAssignments,
  removeAllAssignmentClasses,
  addTotalWordsToAssignment,
} from "./teacherDataReducer";
import firebase from "../../services/firebase/FirebaseService";

class TeacherStateService {
  // List of most of the watch functions
  cleanup: Array<() => void> = [];
  // List of student watches
  // Each watch needs to be able to be removed individually on deletion of the student
  studentWatches: Record<string, () => void> = {};
  // List of shared assignment watches
  // Each watch needs to be able to be removed individually on deletion of the assignment
  sharedAssignmentWatches: Record<string, () => void> = {};

  async startWatch(teacherId: string) {
    // Returns the number of total words in an assignment
    const getTotalAssignmentWords = async (bookId: string) => {
      // Get pages in assignment
      const pages = await firebase.getPages(bookId);
      // Array of all words
      let words: string[] = [];
      // For each page
      for (const page of pages) {
        // Check page has content
        if (page.content) {
          const cleanedWords = page.content
            // Remove punctuation
            .replace(/^[A-Za-z0-9 -]/, "")
            // Split on ends and beginnings of words
            .split(/(\b[^\s]+\b)/)
            // Filter out spaces
            .filter(obj => obj !== " ");
          // Add words to array of all words
          words = words.concat(cleanedWords);
        }
      }
      // Length of all words array is equal to the number of words
      return words.length;
    };

    // Track the number of watches that we're waiting on to start
    let activeWatches = 0;
    const registerWatch = () => (activeWatches += 1);
    const clearWatch = () => {
      activeWatches -= 1;
      if (activeWatches <= 0) {
        finishLoadingTeacher();
      }
    };

    // Register teacher assignments watch
    registerWatch();
    // Watch all assignments the teacher has created (includes archived assignments)
    this.cleanup.push(
      FirebaseService.watchTeacherAssignments(teacherId, async assignments => {
        // List of shared assignments on Teacher State before it is updated,
        // Used to prevent double watches and unnecessary total word calculations
        const previousAssignments = Object.keys(store.getState().teacherState.assignments);
        // For each assignment
        for (const assignment of assignments) {
          // If assignment does NOT already exists on Teacher State, calculate total words
          // This check means total words is only calculated on the first run, not on updates
          if (!previousAssignments.includes(assignment.id)) {
            // Add assignment
            // No assignment the teacher has created will ever be deleted from a teacher, it can only be archived
            addAssignment(assignment);
            // Calculate total words in assignment
            getTotalAssignmentWords(assignment.bookId).then((totalWords: number) => {
              addTotalWordsToAssignment(assignment.id, totalWords);
            });
            // If the assignment is already on Teacher State, this is an update
          } else {
            // Update assignment details
            addAssignment(assignment);
          }
        }
        // Clear teacher assignments watch
        clearWatch();
      }),
    );

    // Register shared folder on user watch
    registerWatch();
    // Watch the shared assignment folder on the user
    this.cleanup.push(
      FirebaseService.watchUserSharedAssignments(teacherId, async sharedAssignments => {
        // List of shared assignments on Teacher State before it is updated,
        // Used to prevent double watches, unnecessary total word calculations, and remove assignments no longer being shared with the teacher
        const previousSharedAssignments = store.getState().teacherState.sharedAssignments ?? [];
        // For each shared assignment in updated list
        for (const sharedAssignment of sharedAssignments) {
          // If assignment is not already in the db
          if (!previousSharedAssignments.includes(sharedAssignment)) {
            // Register shared assignment watch
            registerWatch();
            // Watch shared assignment
            this.sharedAssignmentWatches[sharedAssignment] = FirebaseService.watchAssignment(
              sharedAssignment,
              async (_assignmentId, assignmentDetails) => {
                // If assignment does NOT already exists on Teacher State, calculate total words
                // This check means total words is only calculated on the first run, not on updates
                if (!previousSharedAssignments.includes(sharedAssignment)) {
                  // Add assignment
                  addAssignment(assignmentDetails);
                  // Calculate total words in assignment
                  getTotalAssignmentWords(assignmentDetails.bookId).then((totalWords: number) => {
                    addTotalWordsToAssignment(assignmentDetails.id, totalWords);
                  });
                  // If the assignment is already on Teacher State, this is an update
                } else {
                  // Update assignment details
                  addAssignment(assignmentDetails);
                }
                clearWatch();
              },
            );
          }
        }
        // Stop assignment watch and remove from Teacher State any assignments which are no longer shared with teacher
        for (const assignment of previousSharedAssignments) {
          if (!sharedAssignments.includes(assignment)) {
            this.cleanupSharedAssignmentWatch(assignment);
          }
        }
        // Set list of shared assignments on Teacher State
        setSharedAssignments(sharedAssignments);
      }),
    );
    // Clear shared assignments on user watch
    clearWatch();

    // Register starred assignment folder watch
    registerWatch();
    // Watch star folder to get ids of assignments the teacher has starred
    this.cleanup.push(
      FirebaseService.watchTeacherAssignmentFolder(teacherId, "Star", async assignmentFolders => {
        // Set list of starred assignments on Teacher State
        // The db will never return more than one folder, so use first folder in array\
        const rootFolder = assignmentFolders[0];
        if (rootFolder) {
          setStarredAssignments(rootFolder.childAssignments);
        }
      }),
    );
    // Clear starred assignment folder watch
    clearWatch();

    // Register classes watch
    registerWatch();
    // Watch classrooms
    this.cleanup.push(
      FirebaseService.watchTeacherClasses(teacherId, classrooms => {
        // Set classrooms on Teacher State
        setTeacherClassrooms(classrooms);

        // If no classes, finish loading
        if (classrooms.length === 0) {
          finishLoadingTeacher();
          return;
        }
        // All students on state before it is updated, used to remove students no longer in any class from state
        const previousStudents = store.getState().teacherState.students;
        // Array of all current students in all classrooms, used to remove students no longer in any class from state
        const currentStudents = [];
        // For each classroom watch the assignments and each student
        for (const classroom of classrooms) {
          // Register classroom watch
          registerWatch();
          // If assignments on classroom exist
          if (classroom.assignments) {
            // Register classroom assignments watch
            registerWatch();
            // Watch assignments on classroom
            this.cleanup.push(
              FirebaseService.watchClassroomAssignments(classroom.id, async classroomAssignmentDetails => {
                // For each assignment's classroom details
                for (const classroomAssignmentDetail of classroomAssignmentDetails) {
                  // Add assignment's classroom details to Teacher State
                  addAssignmentClass(classroomAssignmentDetail);
                }
                // Remove the class from any assignments on Teacher State that are no longer on the list of class assignments
                removeClassFromAssignmentClasses(classroom.id, classroom.assignments);

                // Clear classroom assignments watch
                clearWatch();
              }),
            );
          }

          // If classroom has students
          if (classroom.students) {
            // Get an array of the studentIds in the class
            const studentIdArray = Object.keys(classroom.students);
            // Push studentIds to array of all current students in all classrooms
            currentStudents.push(...studentIdArray);
            // For each student in the class

            for (const student of studentIdArray) {
              // Check to see if watch on the student already exists
              // This prevents multiple watches running on the same student
              // Only proceed if watch DOESN'T exist
              if (!this.studentWatches[student]) {
                // Verify student is in class
                if (classroom.students[student] === true) {
                  // Register the student watch
                  registerWatch();
                  // Add watch to studentWatches object, where the key is the studentId and the value is the watch function
                  this.studentWatches[student] = FirebaseService.watchUser(student, studentProfile => {
                    // Add student to Teacher State
                    addStudentToTeacher(studentProfile);
                  });
                  // Clear the student watch
                  clearWatch();
                }
              }
            }
          }
          // Clear the classroom watch
          clearWatch();
        }
        // Stop student watch and remove from Teacher State any students who are no longer in any class
        for (const student in previousStudents) {
          if (!currentStudents.includes(student)) {
            this.cleanupStudentWatch(student);
          }
        }
      }),
    );
    // Clear classes watch
    clearWatch();
  }

  endWatch() {
    // For each watch in cleanup
    for (const cleanup of this.cleanup) {
      // Call watch function
      cleanup();
    }
    // Empty array after all watch functions have been called
    this.cleanup = [];

    // Cleanup studentWatches
    this.cleanupStudentWatches();
    // Cleanup sharedAssignmentWatches
    this.cleanupSharedAssignmentWatches();
    // Remove data from state
    setTeacherClassrooms([]);
    setSharedAssignments([]);
    setStarredAssignments([]);
    removeAllAssignments();
    removeAllAssignmentClasses();
  }

  cleanupStudentWatches() {
    // For each student in studentWatches
    for (const studentId in this.studentWatches) {
      // Clean up individual student watch
      this.cleanupStudentWatch(studentId);
    }
  }

  cleanupStudentWatch(studentId: string) {
    // Call watch function
    if (this.studentWatches[studentId]) {
      this.studentWatches[studentId]?.();
    }
    // Remove watch from studentWatches object
    delete this.studentWatches[studentId];
    // Remove Student from state
    removeStudentFromTeacher(studentId);
  }

  cleanupSharedAssignmentWatches() {
    // For each sharedAssignment in sharedAssignmentWatches
    for (const assignmentId in this.sharedAssignmentWatches) {
      // Clean up individual sharedAssignment watch
      this.cleanupSharedAssignmentWatch(assignmentId);
    }
  }

  cleanupSharedAssignmentWatch(assignmentId: string) {
    // Call watch function
    this.sharedAssignmentWatches[assignmentId]?.();
    // Remove watch from sharedAssignmentWatches object
    delete this.sharedAssignmentWatches[assignmentId];
    // Remove Assignment from state
    removeAssignment(assignmentId);
  }
}

export default TeacherStateService;
